import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppHeader } from "@workspace/ui/components/app-header";
import { Button } from "@workspace/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuGroup,
	DropdownMenuLabel,
} from "@workspace/ui/components/dropdown-menu";
import { ThemeProvider } from "@workspace/ui/theme";
import { UserRound } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { PasswordDrawer } from "./components/account-password-drawer";
import { SelectControl } from "./components/select-control";
import { SessionProvider, useSession, useWorkspace } from "./lib/dajia-session";
import Login from "./pages/dajia-login";
const DajiaAdmin = lazy(() => import("./pages/dajia-admin"));
const GeoReports = lazy(() => import("./pages/dajia-geo-reports"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const Users = lazy(() => import("./pages/dajia-users"));
const Workspaces = lazy(() => import("./pages/dajia-workspaces"));
const navigation = [
	{ label: "可视化大屏", to: "/dashboard" },
	{ label: "监测配置", to: "/monitoring/settings" },
	{ label: "提问设置", to: "/geo/questions" },
	{ label: "基础事实", to: "/knowledge/facts" },
	{ label: "GEO 报告", to: "/geo/reports" },
	{ label: "账号管理", to: "/accounts" },
];
function SessionHeader() {
	const { signOut, switchAccount, switchWorkspace } = useSession(),
		{ user, workspace, workspaces, read_only } = useWorkspace();
	const [notice, setNotice] = useState(""),
		[password, setPassword] = useState(false);
	return (
		<>
			<AppHeader
				accent="大模型可见度监测"
				descriptor={workspace.name}
				navigation={[
					...(workspaces.length > 1
						? [{ label: "单位总览", to: "/workspaces" }]
						: []),
					...navigation,
				].filter(
					(n) =>
						(n.to !== "/accounts" || !read_only) &&
						(user.role === "admin" ||
							["/dashboard", "/geo/reports"].includes(n.to)),
				)}
				product="GEO 看板"
				user={{ name: user.name }}
				onLogout={() => {
					void signOut().catch(() => setNotice("退出未完成，请重试。"));
				}}
				actions={
					<div className="flex items-center gap-3">
						<DropdownMenu>
							<DropdownMenuTrigger
								render={
									<Button
										aria-label="账号操作"
										className="sm:hidden"
										size="icon"
										variant="ghost"
									/>
								}
							>
								<UserRound />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuGroup>
									<DropdownMenuLabel>
										{workspace.name} · {user.name}
									</DropdownMenuLabel>
									<DropdownMenuItem onClick={switchAccount}>
										切换账号
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => setPassword(true)}>
										修改密码
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
						<span className="hidden text-sm text-muted-foreground lg:inline">
							{workspace.name} · {user.name}
						</span>
						<Button
							className="hidden sm:inline-flex"
							variant="ghost"
							size="sm"
							onClick={switchAccount}
						>
							切换账号
						</Button>
						<Button
							className="hidden sm:inline-flex"
							variant="ghost"
							size="sm"
							onClick={() => setPassword(true)}
						>
							修改密码
						</Button>
						{notice && (
							<span role="alert" className="text-xs">
								{notice}
							</span>
						)}
					</div>
				}
			/>
			{workspaces.length > 1 && (
				<div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
					<span className="text-sm text-muted-foreground">查看单位</span>
					<SelectControl
						label="查看单位"
						value={workspace.id}
						onChange={(id) => void switchWorkspace(id)}
						options={workspaces.map((w) => ({
							value: w.id,
							label: w.id === "insurance" ? "大家保险集团" : w.name,
						}))}
						className="w-44"
					/>
					<span className="text-xs text-muted-foreground">
						{read_only
							? "当前查看本单位提问、监测与基础事实"
							: "集团账号可查看全部单位"}
					</span>
				</div>
			)}
			{password && <PasswordDrawer onClose={() => setPassword(false)} />}
		</>
	);
}
function WorkspaceApp() {
	const { user } = useWorkspace();
	// A new signed-in identity gets a new cache. Responses still in flight for the
	// previous account cannot populate the next organisation's pages.
	const [client] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: { retry: 1, staleTime: 30000, refetchInterval: 30000 },
				},
			}),
	);
	return (
		<QueryClientProvider client={client}>
			<SessionHeader />
			<Suspense
				fallback={
					<main className="mx-auto max-w-6xl p-8 text-muted-foreground">
						正在加载…
					</main>
				}
			>
				<Routes>
					<Route path="/" element={<Navigate to="/dashboard" replace />} />
					<Route path="/login" element={<Navigate to="/dashboard" replace />} />
					<Route path="/dashboard" element={<Dashboard />} />
					<Route path="/workspaces" element={<Workspaces />} />
					<Route path="/geo/reports" element={<GeoReports />} />
					<Route
						path="/opinion/reports"
						element={<Navigate to="/geo/reports" replace />}
					/>
					<Route
						path="/accounts"
						element={
							user.role === "admin" ? (
								<Users />
							) : (
								<Navigate to="/dashboard" replace />
							)
						}
					/>
					<Route
						element={
							user.role === "admin" ? (
								<DajiaAdmin />
							) : (
								<Navigate to="/dashboard" replace />
							)
						}
					>
						<Route path="/monitoring/settings" />
						<Route path="/geo/questions" />
						<Route path="/knowledge/facts" />
					</Route>
					<Route path="*" element={<Navigate to="/dashboard" replace />} />
				</Routes>
			</Suspense>
		</QueryClientProvider>
	);
}
function SessionGate() {
	const { session, error, refresh } = useSession();
	if (!session) {
		return (
			<main className="mx-auto max-w-lg space-y-4 p-10 text-center">
				<p>{error || "正在恢复登录状态…"}</p>
				{error && <Button onClick={() => void refresh()}>重新连接</Button>}
			</main>
		);
	}
	if (!session.authenticated) {
		return <Login />;
	}
	return <WorkspaceApp key={`${session.workspace.id}/${session.user.id}`} />;
}
export function App() {
	return (
		<ThemeProvider>
			<BrowserRouter>
				<SessionProvider>
					<SessionGate />
				</SessionProvider>
			</BrowserRouter>
		</ThemeProvider>
	);
}
